<script>
    import { onDestroy } from 'svelte';
    import BaseEditor from '../base/BaseEditor.svelte';
    import { baseEditorTheme, scrollbarStyles } from '../base/EditorTheme.js';
    import { createTermHighlighter, termStyles } from '../../../extensions/codemirror/termHighlighter.js';
    import { createAmbiguousTermHighlighter, ambiguousTermStyles } from '../../../extensions/codemirror/ambiguousTermHighlighter.js';
    import { createAmbiguousTermTooltip } from '../../../extensions/codemirror/ambiguousTermTooltip.js';
    import { SearchHighlighterCodeMirror } from '../../../extensions/codemirror/searchHighlighter.js';
    import { patentCoreExtension } from '../../../extensions/codemirror/patentCoreWords.js';
    import { claimsStore, editorStateStore, termsStore } from '../../../stores/editorsStore.js';
    import { EditorView } from '@codemirror/view';
    import { backendService } from '../../../services/backendService.js';
    import { ClaimsTermUpdateService } from '../../../services/claimsTermUpdateService';
    import { textFormattingExtension } from '../../../extensions/codemirror/textFormatting.js';
    import { inlineCommentExtension } from '../../../extensions/codemirror/inlineCommentHighlighter.js';
    import { commentAnchor } from '../../../extensions/codemirror/commentAnchor.js';
    import { createTermAutocomplete } from '../../../extensions/codemirror/termAutocomplete.js';
    import { claimNumberingExtension } from '../../../extensions/codemirror/claimNumbering.js';
    import { claimNumberHighlighterExtension } from '../../../extensions/codemirror/claimNumberHighlighter.js';
    import { createClaimDependencyHighlighter, updateClaimDependencies, setClaimAnalysisFailed } from '../../../extensions/codemirror/claimDependencyHighlighter.js';
    import { claimDependencyAutoUpdateService } from '../../../services/claimDependencyAutoUpdateService.js';
    import { createClaimClickDetection } from '../../../extensions/codemirror/claimClickDetection.js';
    import { createClaimIndentationExtension } from '../../../extensions/codemirror/claimIndentation.js';
    import { createSpellcheckExtension } from '../../../extensions/codemirror/spellcheck.js';
    import { claimsSettings, termsSettings, settingsStore } from '../../../stores/settingsStore.js';
    import { cursorStore } from '../../../stores/editorsStore.js';
    import { commentsStore } from '../../../stores/commentsStore.js';
    import { validationCacheStore } from '../../../stores/validationCacheStore.js';
    import { spellcheckService } from '../../../services/spellcheckService.js';
    import ClaimValidationErrors from './ClaimValidationErrors.svelte';
    import { termAutoScrollService } from '../../../services/termAutoScrollService.js';

    export let editorView;
    let content;
    let claimsTermService;
    let wordCount = 0;
    let editorKey = 0; // Key to force re-render when settings change
    let unsubscribeTerms; // Store subscription for cleanup
    let forceHeavyRefreshNext = false; // Flag to force heavy refresh after claim dependency fix
    let debounceTimer; // Timer for debouncing analyze-claims requests
    let validationErrors = []; // Store validation errors from backend
    let baseEditor; // Reference to BaseEditor component
    
    // Expose undo/redo functions for external use
    export function performUndo() {
        if (baseEditor) {
            baseEditor.performUndo();
        }
    }
    
    export function performRedo() {
        if (baseEditor) {
            baseEditor.performRedo();
        }
    }

    // Function to calculate word count
    function calculateWordCount(text) {
        if (!text || text.trim() === '') return 0;
        // Filter out inline comment lines before counting
        const nonCommentText = text.split('\n')
            .filter(line => !line.trimStart().startsWith('#'))
            .join('\n');
        // Match words - must contain at least one letter or number
        // This prevents standalone punctuation from being counted as words
        const words = nonCommentText.trim().match(/\b[\w'-]+\b/g);
        return words ? words.length : 0;
    }

    // Initialize term service
    $: {
        if (!claimsTermService) {
            claimsTermService = new ClaimsTermUpdateService(termsStore, claimsStore);
        }
    }

    // Expose function to update validation errors from external components
    function updateValidationErrors(errors) {
        console.log('[ClaimsEditor] Updating validation errors from external source:', errors);
        validationErrors = errors;
    }
    
    // Make the function available globally
    if (typeof window !== 'undefined') {
        window.updateClaimsValidationErrors = updateValidationErrors;
    }
    
    // Initialize spellcheck service on mount
    let spellcheckInitialized = false;

    async function initializeSpellcheck() {
        if (!spellcheckInitialized) {
            await spellcheckService.initialize();
            spellcheckInitialized = true;
        }
    }

    // Call on mount (non-blocking - loads dictionaries in background)
    initializeSpellcheck().catch(err => {
        console.error('Failed to initialize spellcheck:', err);
    });

    // Cleanup on component destroy
    onDestroy(() => {
        if (claimsTermService) {
            claimsTermService.destroy();
        }
        // Clean up debounce timer
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }
        // Clean up window references
        if (window.claimsEditor) {
            window.claimsEditor = null;
        }
        if (window.setForceHeavyRefreshNext) {
            window.setForceHeavyRefreshNext = null;
        }
        if (window.updateClaimsValidationErrors) {
            window.updateClaimsValidationErrors = null;
        }
        // Unsubscribe from termsStore
        if (unsubscribeTerms) {
            unsubscribeTerms();
        }
    });

    // Reactive store subscription
    $: content = $claimsStore.content;
    $: {
        console.log('ClaimsEditor - content updated:', { 
            content: content, 
            type: typeof content,
            length: content?.length || 0, 
            preview: content?.substring(0, 100) || 'NO_CONTENT' 
        });
    }
    
    // Update word count when content changes from store
    $: wordCount = calculateWordCount(content);
    
    let previousAutoNumberSetting = $claimsSettings.autoNumberSubitems;
    $: if ($claimsSettings.autoNumberSubitems !== previousAutoNumberSetting) {
        previousAutoNumberSetting = $claimsSettings.autoNumberSubitems;
        const cursorPos = editorView?.state.selection.main.head;
        editorKey++;
        setTimeout(() => {
            if (editorView && cursorPos !== undefined) {
                editorView.dispatch({
                    selection: { anchor: cursorPos, head: cursorPos }
                });
                editorView.focus();
            }
        }, 50);
    }

    // Claims analysis - relies on backend's smart refresh strategy with debouncing
    async function analyzeClaimsContent(text) {
        // Skip if content is too short to be meaningful
        if (!text || text.trim().length < 20) {
            return;
        }
        
        try {
            termsStore.setAnalyzingTerms(true);
            
            const forceHeavy = forceHeavyRefreshNext;
            if (forceHeavy) {
                forceHeavyRefreshNext = false;
            }
            
            // Check patent type and log it
            const patentType = $settingsStore.patentType;
            const isProvisional = patentType === 'provisional';
            console.log('[ClaimsEditor] Patent type:', patentType, 'is_provisional:', isProvisional);
            
            const result = await backendService.analyzeClaimsText(text, forceHeavy, {
                is_provisional: isProvisional,
                onProgress: (progressData) => {
                    // Check for validation errors
                    if (progressData.validation_errors) {
                        console.log('[ClaimsEditor] Validation errors received:', progressData.validation_errors);
                        validationErrors = progressData.validation_errors;
                        
                        // Cache the validation result for other components
                        validationCacheStore.setCachedValidation(text, { validation_errors: progressData.validation_errors });
                        
                        // Check if there are blocking errors
                        const hasBlockingError = validationErrors.some(err => err.blocking);
                        if (hasBlockingError) {
                            console.error('[ClaimsEditor] BLOCKING validation errors found:', validationErrors.filter(e => e.blocking));
                            termsStore.setAnalyzingTerms(false);
                            return; // Stop processing if blocking errors
                        }
                    }
                    
                    if (progressData.term_counts && Object.keys(progressData.term_counts).length > 0 && !progressData.isComplete) {
                        claimsTermService.updateTermsProgressive(progressData.term_counts, text, progressData);
                    }
                }
            });
            
            // Handle validation errors from the result
            if (result?.validation_errors) {
                console.log('[ClaimsEditor] Final validation errors from result:', result.validation_errors);
                validationErrors = result.validation_errors;
                // Cache the final validation result
                validationCacheStore.setCachedValidation(text, { validation_errors: result.validation_errors });
            } else {
                // Clear validation errors if none returned
                console.log('[ClaimsEditor] No validation errors, clearing any existing errors');
                validationErrors = [];
                // Cache the empty validation result
                validationCacheStore.setCachedValidation(text, { validation_errors: [] });
            }
            
            if (result?.term_counts) {
                claimsTermService.updateTerms(result.term_counts, text, result);
            } else if (result?.terms) {
                claimsTermService.updateTerms(result.terms, text, result);
            }
        } catch (error) {
            // Clear analyzing state on error
            termsStore.setAnalyzingTerms(false);
            
            // Silently ignore errors for automatic analysis
            // Only log if it's not a 400 error (which happens for very short content)
            if (!error.message.includes('400')) {
                console.error('Claims analysis error:', {
                    message: error.message,
                    stack: error.stack
                });
            }
        }
    }

    // Debounced version of analyzeClaimsContent to avoid excessive API calls
    function debouncedAnalyzeClaimsContent(text) {
        // Clear existing timer
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }
        
        // Set new timer - wait 1.5 seconds after user stops typing
        debounceTimer = setTimeout(() => {
            analyzeClaimsContent(text);
        }, 1500);
    }

    function handleContentChange(newContent) {
        if (newContent !== $claimsStore.content) {
            claimsStore.update(store => ({ ...store, content: newContent }));
            editorStateStore.update(s => ({
                ...s,
                isDirty: true
            }));
            
            // Mark that claims have been modified since last heavy refresh
            // This will hide term highlights until the next heavy refresh
            termsStore.markClaimsModified();
            
            // Only auto-analyze if auto-refresh is enabled
            if ($termsSettings.autoRefresh) {
                debouncedAnalyzeClaimsContent(newContent);
            }
        }
        wordCount = calculateWordCount(newContent);
    }

    // Track changes functionality temporarily removed - will be reimplemented
    
    $: if (editorView && !unsubscribeTerms) {
        unsubscribeTerms = termsStore.subscribe(async ($termsStore) => {
            const hasTerms = $termsStore.termEntries && $termsStore.termEntries.size > 0;
            const hasDependencies = $termsStore.claimDependencies && Object.keys($termsStore.claimDependencies).length > 0;
            
            if (hasDependencies) {
                updateClaimDependencies(editorView, $termsStore.claimDependencies);
                
                const result = await claimDependencyAutoUpdateService.processAutoUpdates(
                    $termsStore.claimDependencies,
                    editorView
                );
            } else if ($termsStore.isAnalyzingTerms) {
                setClaimAnalysisFailed(editorView, false);
            } else if (!hasTerms && !hasDependencies) {
                setClaimAnalysisFailed(editorView, true);
            }
        });
    }
    
    // Add basic editor commands
    $: if (editorView) {
        
        // Initialize commands object
        editorView.commands = {
            
            focus: function() {
                if (editorView) {
                    editorView.focus();
                }
                return true;
            },
            // Add text selection method for search navigation
            setTextSelection: function({ from, to }) {
                if (editorView) {
                    editorView.dispatch({
                        selection: { anchor: from, head: to }
                    });
                    editorView.focus();
                }
                return true;
            },
            // Empty placeholder for setSearchResults to maintain API compatibility
            setSearchResults: function() {
                // Clear any existing search highlights
                if (editorView) {
                    SearchHighlighterCodeMirror.clearSearchResults(editorView);
                }
                return true;
            }
        };
        
        // Register the editor with the auto-scroll service
        termAutoScrollService.setClaimsEditor(editorView);
        
        // Expose to window for comment form access
        window.claimsEditor = editorView;
        
        window.setForceHeavyRefreshNext = () => {
            forceHeavyRefreshNext = true;
        };
    }
</script>

<div class="claims-editor" data-testid="claims-editor">
    <div class="claims-header">
        <h2>Claims</h2>
        <ClaimValidationErrors 
            bind:errors={validationErrors}
            on:dismiss={(e) => console.log('Dismissed error at index:', e.detail.index)}
        />
    </div>
    <div class="editor-container">
        {#key editorKey}
        <BaseEditor
            bind:this={baseEditor}
            bind:editorView
            {content}
            onContentChange={handleContentChange}
            onFocus={() => {
                cursorStore.update(store => ({ ...store, activeEditor: 'claims' }));
            }}
            ariaLabel="Claims editor"
            extensions={[
                claimNumberingExtension(), // Auto-numbering must come first to handle Enter key
                baseEditorTheme,
                scrollbarStyles,
                patentCoreExtension,
                claimNumberHighlighterExtension, // Highlight claim numbers like patent keywords
                createTermHighlighter(termsStore),
                termStyles,
                createAmbiguousTermHighlighter(termsStore),
                ambiguousTermStyles,
                createAmbiguousTermTooltip(), // Unified tooltip for all ambiguous term errors
                SearchHighlighterCodeMirror.extension,
                textFormattingExtension,
                inlineCommentExtension,
                commentAnchor({
                    onCommentClick: (commentId, anchorId) => {
                        // Open the comments panel if it's not already open
                        commentsStore.open();
                        // Select the clicked comment
                        commentsStore.selectComment(commentId);
                    }
                }),
                createTermAutocomplete(termsStore),
                createClaimDependencyHighlighter(),
                createClaimClickDetection(),
                createClaimIndentationExtension(),
                createSpellcheckExtension()
            ]}
        />
        {/key}
    </div>
    <div class="word-count-indicator" aria-label="Word count">
        {wordCount} {wordCount === 1 ? 'word' : 'words'}
    </div>
</div>

<style>
    .claims-editor {
        position: relative;
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: visible;
        border-radius: var(--border-radius-lg);
        box-shadow: none;
    }

    .claims-header {
        background: transparent;
    }
    
    h2 {
        padding: var(--spacing-2) var(--spacing-4);
        margin: 0;
        font-size: var(--font-size-xl);
        font-weight: var(--font-weight-semibold);
        text-align: center;
        color: var(--color-text-muted);
        background: transparent;
    }

    .editor-container {
        position: relative;
        flex: 1;
        overflow: hidden;
        background: transparent;
    }

    /* Dark mode cursor fix */
    :global([data-theme="dark"]) .editor-container :global(.cm-cursor),
    :global([data-theme="dark"]) .editor-container :global(.cm-dropCursor) {
        border-left-color: var(--color-text-primary) !important;
    }
    
    :global([data-theme="dark"]) .editor-container :global(.cm-content) {
        caret-color: var(--color-text-primary) !important;
    }
    
    :global([data-theme="dark"]) .editor-container :global(.cm-editor) {
        caret-color: var(--color-text-primary) !important;
    }
    
    /* Remove global hanging indent - we'll handle it more specifically */
    .editor-container :global(.cm-line) {
        display: block !important;
    }
    
    /* Claim number highlighting styles */
    .editor-container :global(.cm-claim-number) {
        font-weight: bold !important;
        color: var(--color-patent-keyword) !important;
    }
    
    .word-count-indicator {
        position: absolute;
        right: var(--spacing-2);
        bottom: calc(var(--spacing-4) * -1);
        z-index: 1;
        padding: var(--spacing-1) var(--spacing-2);
        font-size: var(--font-size-xs);
        color: var(--color-text-tertiary);
        pointer-events: none;
        transition: var(--transition-opacity);
    }
</style>